// src/components/AGTUps/products/AGTCouncil-products/Stats.js

import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, CircularProgress } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { Brain, Lightbulb, Clock, Book } from 'lucide-react';
import consultService from '../../services/AGTUps-product-services/consultService';

// Paleta de colores inspirada en Apple
const getAppleColors = (theme) => ({
  background: theme.palette.mode === 'dark' 
    ? 'rgba(28, 28, 30, 0.95)' 
    : 'rgba(255, 255, 255, 0.95)',
  textPrimary: theme.palette.mode === 'dark' 
    ? '#FFFFFF' 
    : '#1D1D1F',
  textSecondary: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.7)' 
    : 'rgba(29, 29, 31, 0.7)'
});

// Gradientes sutiles para cada estadística (light/dark)
const appleGradients = {
  consultations: {
    light: 'linear-gradient(145deg, #EBF5FF, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1E3A8A, #2D3A8F)'
  },
  successRate: {
    light: 'linear-gradient(145deg, #ECFDF5, #FFFFFF)',
    dark: 'linear-gradient(145deg, #065F46, #166F54)'
  },
  avgMessages: {
    light: 'linear-gradient(145deg, #F5F3FF, #FFFFFF)',
    dark: 'linear-gradient(145deg, #5B21B6, #6D33C5)'
  },
  finalised: {
    light: 'linear-gradient(145deg, #FFFBEB, #FFFFFF)',
    dark: 'linear-gradient(145deg, #854D0E, #96591A)'
  }
};

// Contenedor principal con estilo Apple
const StatsContainer = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
    background: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : getAppleColors(theme).background,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'blur(20px)',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'blur(20px)',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    borderRadius: isWallpaperMode ? theme.spacing(2) : 0,
  };
});

// Paper principal con estilo minimalista de Apple
const StatsPaper = styled(Paper)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    borderRadius: theme.spacing(3),
    padding: theme.spacing(4),
    background: 'transparent',
    boxShadow: 'none',
    color: getAppleColors(theme).textPrimary,
    width: '100%',
    maxWidth: '1200px',
    border: isWallpaperMode ? 'none' : 'none', // Keep consistent with existing transparent design
  };
});

// Tarjeta individual con estilo Apple
const StatBox = styled(Box)(({ theme, bgKey }) => {
  const mode = theme.palette.mode;
  const gradient = appleGradients[bgKey]?.[mode === 'dark' ? 'dark' : 'light'];
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    borderRadius: theme.spacing(2.5),
    padding: theme.spacing(3),
    background: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : gradient,
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center'
  };
});

// Componente de Loading
const LoadingOverlay = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '200px',
  width: '100%'
});

const iconStyle = (color) => ({
  marginBottom: 2,
  color: color
});

const Stats = () => {
  const theme = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const res = await consultService.getConsultationStats();
        if (res.success && res.data) {
          setStats(res.data);
        }
      } catch (error) {
        console.error('Error fetching consultation stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  return (
    <StatsContainer>
      <StatsPaper>
        <Typography
          variant="h2"
          sx={{
            mb: 1,
            fontWeight: 600,
            fontSize: '2.5rem',
            letterSpacing: '-0.5px'
          }}
        >
          Quick Stats
        </Typography>
        
        <Typography
          variant="body1"
          sx={{
            mb: 6,
            color: getAppleColors(theme).textSecondary,
            fontSize: '1.1rem',
            fontWeight: 400
          }}
        >
          Overview of your consultation metrics
        </Typography>

        {loading ? (
          <LoadingOverlay>
            <CircularProgress />
          </LoadingOverlay>
        ) : stats ? (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <StatBox bgKey="consultations">
                <Brain 
                  size={36} 
                  style={iconStyle(theme.palette.mode === 'dark' ? '#60A5FA' : '#3b82f6')} 
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 500,
                    letterSpacing: '-1px',
                    fontSize: '2.25rem',
                    mt: 2
                  }}
                >
                  {stats.totalConsultations}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 500,
                    color: getAppleColors(theme).textSecondary,
                    mt: 0.5
                  }}
                >
                  Consultations
                </Typography>
              </StatBox>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <StatBox bgKey="successRate">
                <Lightbulb 
                  size={36} 
                  style={iconStyle(theme.palette.mode === 'dark' ? '#34D399' : '#10b981')} 
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 500,
                    letterSpacing: '-1px',
                    fontSize: '2.25rem',
                    mt: 2
                  }}
                >
                  {stats.successRate}%
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 500,
                    color: getAppleColors(theme).textSecondary,
                    mt: 0.5
                  }}
                >
                  Success Rate
                </Typography>
              </StatBox>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <StatBox bgKey="avgMessages">
                <Clock 
                  size={36} 
                  style={iconStyle(theme.palette.mode === 'dark' ? '#A78BFA' : '#8b5cf6')} 
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 500,
                    letterSpacing: '-1px',
                    fontSize: '2.25rem',
                    mt: 2
                  }}
                >
                  {stats.avgMessagesPerConsultation}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 500,
                    color: getAppleColors(theme).textSecondary,
                    mt: 0.5
                  }}
                >
                  Avg. Messages
                </Typography>
              </StatBox>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <StatBox bgKey="finalised">
                <Book 
                  size={36} 
                  style={iconStyle(theme.palette.mode === 'dark' ? '#FCD34D' : '#facc15')} 
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 500,
                    letterSpacing: '-1px',
                    fontSize: '2.25rem',
                    mt: 2
                  }}
                >
                  {stats.finalisedConsultations}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 500,
                    color: getAppleColors(theme).textSecondary,
                    mt: 0.5
                  }}
                >
                  Finalised
                </Typography>
              </StatBox>
            </Grid>
          </Grid>
        ) : (
          <Typography 
            variant="body1"
            sx={{
              color: getAppleColors(theme).textSecondary,
              textAlign: 'center',
              py: 4
            }}
          >
            No statistics available.
          </Typography>
        )}
      </StatsPaper>
    </StatsContainer>
  );
};

export default Stats;